import React, { useEffect, useMemo, useRef, useState } from "react";
import lumesAILogo from "./assets/lumesAI.png";
import "./App.css";

function App() {
  // ✅ Safe ID generator for iOS/iPadOS (crypto.randomUUID not always supported)
const makeId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [image, setImage] = useState(null);

  // Store chat messages as bubbles (user + assistant)
  const [chat, setChat] = useState([]);
  const [isSending, setIsSending] = useState(false);

  const messagesEndRef = useRef(null);

  // ✅ Must be state (you cannot reassign const variables inside useEffect)
  const [userId, setUserId] = useState("");
  const [partnerId, setPartnerId] = useState("");

  const initials = useMemo(() => {
    const s = (username || "U").trim();
    const parts = s.split(" ").filter(Boolean);
    const a = parts[0]?.[0] || "U";
    const b = parts[1]?.[0] || "";
    return (a + b).toUpperCase();
  }, [username]);

  useEffect(() => {
    const fetchIds = async () => {
      try {
        const response = await fetch("https://api.lums.online/ids");
        if (response.ok) {
          const data = await response.json();
          setUserId(data.userId || "");
          setPartnerId(data.partnerId || "");
        } else {
          console.error("Failed to fetch IDs");
        }
      } catch (error) {
        console.error("Error fetching IDs:", error);
      }
    };

    fetchIds();
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, isSending]);

  const handleLogin = async () => {
    try {
      const response = await fetch("https://api.lums.online/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // ✅ trim to avoid mobile autofill adding spaces
        body: JSON.stringify({ username: username.trim(), password: password.trim() }),
      });

      if (response.ok) {
        await response.json(); // you can use the returned message if you want
        setIsLoggedIn(true);
        setChat([
          {
            id: makeId(),
            role: "assistant",
            text: "Hi! I’m Lumix. How can I help you today?",
            ts: Date.now(),
          },
        ]);
      } else {
        alert("Invalid credentials!");
      }
    } catch (error) {
      console.error("Login error:", error);
      alert("An error occurred during login. Please try again.");
    }
  };

  const handleSendMessage = async () => {
    const trimmed = message.trim();
    if (!trimmed || isSending) return;

    const outgoing = {
      id: makeId(),
      role: "user",
      text: trimmed,
      ts: Date.now(),
    };

    setChat((prev) => [...prev, outgoing]);
    setMessage("");
    setIsSending(true);

    try {
      const response = await fetch("https://lums.online/chat/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          partnerId,
          content: trimmed,
        }),
      });

      const data = await response.json();

      setChat((prev) => [
        ...prev,
        {
          id: makeId(),
          role: "assistant",
          text: data?.text ?? "(No text field returned)",
          ts: Date.now(),
          raw: data,
        },
      ]);
    } catch (err) {
      setChat((prev) => [
        ...prev,
        {
          id: makeId(),
          role: "assistant",
          text: "Network error. Please try again.",
          ts: Date.now(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    // Enter sends, Shift+Enter new line
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = async () => {
    if (!file || isSending) return;

    setIsSending(true);
    setChat((prev) => [
      ...prev,
      {
        id: makeId(),
        role: "user",
        text: `📎 Uploading file: ${file.name}`,
        ts: Date.now(),
      },
    ]);

    try {
      const formData = new FormData();
      formData.append("file", file);

      formData.append("userId", userId);
      formData.append("partnerId", partnerId);
      formData.append("content", "");

      const response = await fetch("https://lums.online/chat/send", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      setChat((prev) => [
        ...prev,
        {
          id: makeId(),
          role: "assistant",
          text: data?.text ?? "(No text field returned)",
          ts: Date.now(),
          raw: data,
        },
      ]);
      setFile(null);
    } catch (err) {
      setChat((prev) => [
        ...prev,
        {
          id: makeId(),
          role: "assistant",
          text: "File upload failed. Please try again.",
          ts: Date.now(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleImageUpload = async () => {
    if (!image || isSending) return;

    setIsSending(true);
    setChat((prev) => [
      ...prev,
      {
        id: makeId(),
        role: "user",
        text: `🖼️ Uploading image: ${image.name}`,
        ts: Date.now(),
      },
    ]);

    try {
      const formData = new FormData();
      formData.append("image", image);

      formData.append("userId", userId);
      formData.append("partnerId", partnerId);
      formData.append("content", "");

      const response = await fetch("https://lums.online/chat/send", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      setChat((prev) => [
        ...prev,
        {
          id: makeId(),
          role: "assistant",
          text: data?.text ?? "(No text field returned)",
          ts: Date.now(),
          raw: data,
        },
      ]);
      setImage(null);
    } catch (err) {
      setChat((prev) => [
        ...prev,
        {
          id: makeId(),
          role: "assistant",
          text: "Image upload failed. Please try again.",
          ts: Date.now(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="brand">
            <div className="logo-wrap">
              <div className="logo-glow" />
              <div className="logo-badge">
                <img src={lumesAILogo} alt="LumsAI Logo" className="logo" />
              </div>
            </div>
            <div className="brand-text">
              <h1 className="brand-title">LumsAI</h1>
              <p className="brand-subtitle">Private chat console</p>
            </div>
          </div>

          <div className="auth-fields">
            <label>
              Username
              <input
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </label>

            <label>
              Password
              <input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </label>

            <button className="primary-btn" onClick={handleLogin}>
              Login
            </button>

            <div className="auth-hint">
              Tip: Press <kbd>Enter</kbd> after typing your password.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-shell">
      {/* Sidebar (simple) */}
      <aside className="sidebar">
        <div className="sidebar-top">
          <div className="avatar">{initials}</div>
          <div className="me">
            <div className="me-name">{username}</div>
            <div className="me-sub">Connected</div>
          </div>
        </div>

        <div className="sidebar-section">
          <div className="section-title">Chat</div>
          <div className="chat-card active">
            <div className="chat-card-title">Lumix</div>
            <div className="chat-card-sub">Assistant</div>
          </div>
        </div>

        <div className="sidebar-footer">
          <button className="ghost-btn" onClick={() => setIsLoggedIn(false)}>
            Log out
          </button>
        </div>
      </aside>

      {/* Main chat area */}
      <main className="chat-main">
        <header className="chat-header">
          <div className="chat-title">
            <div className="chat-badge chat-logo">
              <img src={lumesAILogo} alt="Lumix Logo" />
            </div>
            <div>
              <div className="chat-name">Lumix</div>
              <div className="chat-status">
                {isSending ? "Thinking…" : "Online"}
              </div>
            </div>
          </div>
        </header>

        <section className="messages">
          {chat.map((m) => (
            <div
              key={m.id}
              className={`msg-row ${m.role === "user" ? "right" : "left"}`}
            >
              <div className={`msg-bubble ${m.role}`}>
                <div className="msg-text">{m.text}</div>

                {m.raw && (
                  <details className="msg-raw">
                    <summary>Details</summary>
                    <pre>{JSON.stringify(m.raw, null, 2)}</pre>
                  </details>
                )}
              </div>
            </div>
          ))}

          {isSending && (
            <div className="msg-row left">
              <div className="msg-bubble assistant typing">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </section>

        <footer className="composer">
          <div className="composer-inner">
            <textarea
              className="composer-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Write a message… (Enter to send, Shift+Enter for new line)"
              rows={1}
            />

            <button
              className="send-btn"
              onClick={handleSendMessage}
              disabled={!message.trim() || isSending}
              title="Send"
            >
              ➤
            </button>
          </div>

          {(file || image) && (
            <div className="attachments">
              {file && (
                <div className="attach-chip">
                  📎 {file.name}
                  <button onClick={() => setFile(null)} aria-label="Remove file">
                    ✕
                  </button>
                </div>
              )}
              {image && (
                <div className="attach-chip">
                  🖼️ {image.name}
                  <button
                    onClick={() => setImage(null)}
                    aria-label="Remove image"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          )}
        </footer>
      </main>
    </div>
  );
}

export default App;